import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import Input from "../../../components/elements/form/input";
import Button from '../../../components/elements/button';
import { useSagesApi } from '../../../store/api/hooks/sageApiHook';
import toast from 'react-hot-toast';
import { useAppLoader } from '../../../_common/pageLoader/appLoaderHook';
import SelectBox from '../../../components/elements/form/selectBox';

interface AssetsGroupMasterFormProps {
    _ref?: any;
    editData?: any;
    onCreate?: () => void;
    onCancel?: () => void;
}

type AssetGroupMasterFormValueType = {
    assetGroupID: 0;
    name: string;
    code: any;
    isWorkOrderable: any;
    assetSubGroupID?: number;
};

const AddSageAssestGM: React.FC<AssetsGroupMasterFormProps> = ({ _ref, editData, onCreate = () => { }, onCancel = () => { } }) => {
    const isworkorderableOptions = [{ label: 'Yes', value: true }, { label: 'No', value: false }];
    const { control, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm<AssetGroupMasterFormValueType>({
        defaultValues: {
            assetGroupID: 0,
            name: '',
            code: '',
            isWorkOrderable: isworkorderableOptions[0].value
        }
    });

    const sageApi: any = useSagesApi();
    const { showLoader, hideLoader } = useAppLoader();
    const [assetSubGroupCode, setassetSubGroupCode] = useState<any>([]);

    const formReset = () => {
        reset({
            assetGroupID: 0,
            name: '',
            code: getAssetSubGroupCode(),
            isWorkOrderable: isworkorderableOptions[0].value
        });
    };

    const onSubmit: SubmitHandler<AssetGroupMasterFormValueType> = formData => {
        showLoader();
        const payload: any = {
            assetGroupID: 0,
            name: formData.name,
            code: formData.code,
            isWorkOrderable: formData.isWorkOrderable,
        };

        if (editData) {
            payload['assetSubGroupID'] = editData.assetSubGroupID;
        }

        sageApi.saveAssetSubGroup(payload, (resp: any) => {
            hideLoader();
            if (resp.successful) {
                toast.success(resp?.message);
                onCreate();
                onCancel();
                formReset();
                if (!editData) getAssetSubGroupCode(); // fetch new code after successful create
            } else {
                toast.error(resp.message || "Connection Failed");
            }
        }, (error: any) => {
            hideLoader();
            toast.error(error);
        });
    };

    const getAssetSubGroupCode = () => {
        const payload: any = { pageSize: 0 };
        sageApi.getAssetSubGroupCode(payload, (resp: any) => {
            setassetSubGroupCode(resp.code);
            setValue('code', resp.code); // set the new code
        }, (error: any) => {
            toast.error(error);
        });
    };

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.reset = formReset;
        }
    }, [_ref]);

    useEffect(() => {
        if (!editData) {
            getAssetSubGroupCode();
        }
    }, [editData]);

    useEffect(() => {
        if (editData == null) {
            formReset();
        }
    }, [editData]);

    useEffect(() => {
        if (editData) {
            setValue('name', editData.assetSubGroupName);
            setValue('code', editData.assetSubGroupCode);
            setValue('isWorkOrderable', editData.isWorkOrderable);
        } else {
            formReset();
        }
    }, [editData]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} ref={_ref} className="space-y-5">
            <div>
                <Controller
                    name="code"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <Input
                            label="Group Code"
                            type="text"
                            value={value}
                            onChange={onChange}
                            disabled // disable editing during update
                        />
                    )}
                />
            </div>
            <div>
                <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Group Name Required" }}
                    render={({ field: { value, onChange } }) => (
                        <Input
                            label="Group Name"
                            type="text"
                            value={value}
                            onChange={onChange}
                            required
                            error={errors.name?.message}
                        />
                    )}
                />
            </div>
            <div>
                <Controller
                    name="isWorkOrderable"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                        <div className="flex items-center gap-2 mt-2">
                            <input
                                type="checkbox"
                                className='w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                            />
                            <p className="font-normal text-[0.938rem] leading-none">Show Asset Group In Task.</p>
                        </div>
                    )}
                />
            </div>

            <div className="flex justify-end gap-2 flex-shrink-0 pt-2 border-t border-gray-200">
                <Button type="button" theme="secondary" onClick={formReset}>
                    Reset
                </Button>
                <Button type="submit">
                    Save
                </Button>
            </div>
        </form>
    );
};

export default AddSageAssestGM;